import React, {useCallback, useContext, useEffect, useState} from 'react';
import * as PropTypes from 'prop-types';

import PlainText from 'ati-ui-react/components/PlainText';
import {interpolate} from 'as-ui-kit/utils/strings';
import { classes } from 'as-ui-kit/utils/styles';
import Button from '@components/button';
import Icon from '@components/icon';
import Link from '@components/link';
import Popup from '@components/popup';

import {ConfigContext, DataContext, LoadingContext, LocaleContext} from 'contexts';
import {useLocalized} from 'utils/useLocalized';
import { catchWithMsg } from 'utils/http';
import {getRemainingTimeToTomorrow} from 'utils/time';
import { PaymentType, PaySource } from 'consts/consts';
import PaySourceSelector from './PaySourceSelector';
import BankCardIcons from './BankCardIcons';
import i18nStrings from './strings';
import styles from './styles.modules.scss';
import {sendMetric} from 'utils/metrics';
import {buildPayByCardRequest, eventMetrics, virtualPayRequest} from 'logic/requestParams';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Loading from 'components/Loading';
import {externalOriginQuery} from 'logic/entities';
import * as vandamus from 'utils/requests/search';
import {makeMetricEventRequest} from 'logic/adapters';

const PayPopup = ({writeMetricEvent, topUpVirtualAccount, onPayPopupAccessCheck}) => {
    const { isMobile, userProfile, licenseInfo } = useContext(DataContext);
    const { mainSiteUrl } = useContext(ConfigContext);
    const { searchResultsIsLoading } = useContext(LoadingContext);

    const locale = useContext(LocaleContext);
    const strings = useLocalized(i18nStrings);
    const [remaining, setRemaining] = useState(getRemainingTimeToTomorrow());
    const [confirmClicked, setConfirmClicked] = useState(false);
    const [selectedSource, setSelectedSource] = useState(PaySource.bankCard);
    const [isPayByCardMetricSent, setPayByCardMetricSent] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isLicenseActive = !licenseInfo.searchAccessPrice && !!licenseInfo.searchAccessRemainingTime;

    const navigateToHomePage = useCallback(() => {
        searchParams.delete(externalOriginQuery);
        navigate({pathname: '/active-truckers/', search: searchParams.toString()});
    }, [navigate, searchParams]);

    useEffect(() => {
        onPayPopupAccessCheck();
    }, [onPayPopupAccessCheck]);

    useEffect(() => {
        if (searchParams.get(externalOriginQuery)) {
            vandamus.writeEvent(makeMetricEventRequest(eventMetrics.tryOpenPopupByExternalLink), mainSiteUrl);
        }
    }, [mainSiteUrl, searchParams]);

    useEffect(() => {
        const isPopupOpenSuccess = userProfile && userProfile.balance >= 0 && isLicenseActive === false && searchResultsIsLoading === false;
        if (searchParams.get(externalOriginQuery)) {
            isPopupOpenSuccess && writeMetricEvent(eventMetrics.openPopupByExternalLink);
        } else {
            isPopupOpenSuccess && writeMetricEvent(eventMetrics.openPopup);
        }
    }, [isLicenseActive, searchParams, searchResultsIsLoading, userProfile, writeMetricEvent]);

    useEffect(() => {
        const timerId = setInterval(() => {
            setRemaining(getRemainingTimeToTomorrow(locale));
        }, 10000);

        return () => clearInterval(timerId);
    }, [locale]);

    useEffect(() => {
        if(isLicenseActive) {
            window.appErrno?.show({text: strings.accessPayed, type: 'warning'});
            navigateToHomePage();
        }
    }, [isLicenseActive, navigateToHomePage, strings]);

    if(Object.keys(licenseInfo).length === 0 || !userProfile || userProfile.balance < 0) return <Loading/>;

    const enoughBalance = userProfile.balance >= licenseInfo.searchAccessPrice?.personal;
    const paymentType = selectedSource === PaySource.bankCard
        ? PaymentType.payByBankCard
        : enoughBalance ? PaymentType.payFromVirtualAccount : PaymentType.topUpVirtualAccount;

    const payFromVirtual = () => vandamus.payFromVirtual(virtualPayRequest, mainSiteUrl);
    const payByBankCard = () => vandamus.payByCard(buildPayByCardRequest(), mainSiteUrl);

    const handlePayConfirm = () => {
        sendMetric('active-carriers-click-pay');
        setConfirmClicked(true);
        switch (paymentType) {
            case PaymentType.payByBankCard: {
                payByBankCard().catch(catchWithMsg).catch(() => setConfirmClicked(false));
                break;
            }
            case PaymentType.payFromVirtualAccount: {
                payFromVirtual().catch(catchWithMsg).catch(() => setConfirmClicked(false));
                break;
            }
            case PaymentType.topUpVirtualAccount: {
                topUpVirtualAccount();
                setConfirmClicked(false);
                break;
            }
        }
    };

    const writeOffError = (() => {
        if (!userProfile) {
            return strings.needAuth;
        }

        if (selectedSource === PaySource.atis && userProfile.forbidWriteOff) {
            return strings.forbidWriteOff;
        }

        return null;
    })();

    const atisesStrings = enoughBalance ? strings.popup.atises.withBalance : strings.popup.atises.withoutBalance;

    const closePopup = () => {
        writeMetricEvent(eventMetrics.closePopup);
        navigateToHomePage();
    };

    return (
        <Popup
            closeOnEscape={false}
            closeOnOverlayClick={false}
            onClose={closePopup}
        >
            <Popup.Header>
                {strings.popup.title}
            </Popup.Header>
            <Popup.Content>
                <div className={styles['pay-sources']}>
                    <PaySourceSelector
                        isActive={selectedSource === PaySource.bankCard}
                        onClick={() => {
                            if (!isPayByCardMetricSent) {
                                sendMetric('active-carriers-click-payment-by-card');
                                setPayByCardMetricSent(true);
                            }
                            setSelectedSource(PaySource.bankCard);
                        }}
                    >
                        <div className={styles['content']}>
                            <BankCardIcons/>
                            <PlainText
                                size="small"
                            >
                                {strings.popup.bankCard}
                            </PlainText>
                        </div>
                    </PaySourceSelector>
                    <PaySourceSelector
                        isActive={selectedSource === PaySource.atis}
                        onClick={() => setSelectedSource(PaySource.atis)}
                    >
                        <div className={styles['content']}>
                            <Icon
                                className={styles['ati-icon']}
                                name="ic_ati-logo"
                                size="l"
                            />
                            <PlainText
                                bold
                                size="small"
                            >
                                {atisesStrings.title}
                            </PlainText>
                            {(isMobile && !enoughBalance) || (
                                <PlainText
                                    size="small"
                                >
                                    {atisesStrings.description}
                                </PlainText>
                            )}
                        </div>
                    </PaySourceSelector>
                </div>

                <PlainText
                    className={classes(styles['price-info'], !isMobile && styles['desktop'])}
                    size="large"
                >
                    {interpolate(strings.popup[paymentType].priceInfo, { price: licenseInfo.searchAccessPrice?.personal })}
                </PlainText>

                <div className={classes(styles['remaining'], !isMobile && styles['desktop'], isMobile && paymentType === PaymentType.topUpVirtualAccount && styles['mobile-top-up'])}>
                    {paymentType !== PaymentType.topUpVirtualAccount && (
                        <PlainText
                            className={styles['time']}
                            size="small"
                        >
                            {interpolate(strings.popup.remaining, { date: remaining.date, hours: remaining.hours, minutes: remaining.minutes })}
                        </PlainText>
                    )}
                </div>
            </Popup.Content>
            {writeOffError && (
                <Popup.Content theme="error">
                    {writeOffError}
                </Popup.Content>
            )}
            <Popup.Footer className={styles['popup-footer']}>
                <Popup.ActionWrapper type="main">
                    <Button
                        fullWidth
                        className={styles['confirm-btn']}
                        disabled={!!writeOffError}
                        freeze={confirmClicked}
                        loading={confirmClicked}
                        theme="primary"
                        onClick={handlePayConfirm}
                    >
                        {strings.popup[paymentType].confirm}
                    </Button>
                </Popup.ActionWrapper>
                <Popup.ActionWrapper type="cancel">
                    <Link
                        onClick={closePopup}
                    >
                        {strings.popup.cancel}
                    </Link>
                </Popup.ActionWrapper>
            </Popup.Footer>
        </Popup>
    );
};

export default PayPopup;

PayPopup.propTypes = {
    topUpVirtualAccount: PropTypes.func,
    writeMetricEvent: PropTypes.func,
    onPayPopupAccessCheck: PropTypes.func,
};
